import { useEffect, useRef, useState, createContext } from "react";
import { CircularProgress } from "@mui/material";
import { useParams, Redirect, Location, Prompt, useHistory } from "react-router-dom";
import { getCachedData, updateEntitiesToCache } from "api/Cache";
import ApiUtils from "api/Utils";
import ViewerEvents from "events/ViewerEvents";
import EmbeddedViewer from "@voxelfarm/voxelfarm-react-viewer";
import AuthService from "services/AuthService";
import { config } from "config";
import Panels from "panels/Main";
import * as Utils from "services/UtilsService";
import { addToCurrentProject, findElement } from "actions/ViewerTools";
import CrossSectionBounds from "events/CrossSectionBounds";
import { CircularProgressWithLabel } from "components";
import viewsStyles from "styles/views";
import { defaultViewProgram } from "constants/viewer";
import { tss } from "tss-react";
import './Viewer.css';
import {
    setActiveProjectData,
    resetActiveProjectData
} from "store/auth";
import { useDispatch, useSelector } from 'react-redux';
import { getAccessLevel } from "actions/ViewerTools";
import _ from 'utils/@lodash';
import { setData } from "store/main/viewerHistory";
import { useJumboDialog } from 'themes/jumbo/@jumbo/components/JumboDialog/hooks/useJumboDialog';

interface Props {
    when?: boolean | undefined;
    navigate: (path: string) => void;
    shouldBlockNavigation: (location: Location) => boolean;
}

const useStyles = tss.create(() => ({
    CircularProgress: {
        position: "absolute",
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        margin: 'auto auto',
        width: 'unset',
        minWidth: '30px',
        minHeight: '30px',
    }
}));


const requestAuthToken = () => {
    return AuthService.getToken();
};

export const ViewerContext = createContext({});

const ViewerLeavingGuard = ({
    when,
    navigate,
    shouldBlockNavigation,
  }: Props) => {
    const [showModal, setShowModal] = useState(false);
    const [lastLocation, setLastLocation] = useState<Location | null>(null);
    const [confirmedNavigation, setConfirmedNavigation] = useState(false);
    const { showDialog, hideDialog } = useJumboDialog();
  
    const handleViewerNavigation = (nextLocation: Location): boolean => {
      if (!confirmedNavigation && shouldBlockNavigation(nextLocation)) {
        showDialog({
            variant: 'confirm',
            title: '',
            content: 'Are you sure you want to exit? Any unsaved changes will be lost.',
            onYes: () => {
                hideDialog();
                setLastLocation(nextLocation);
                setConfirmedNavigation(true);
                ViewerEvents.emit("destroyView", {});
            },
            onNo: () => hideDialog(),
        });
        return false;
      }
      return true;
    };
  
    useEffect(() => {
      confirmedNavigation && lastLocation && navigate(lastLocation.pathname);
    }, [confirmedNavigation, lastLocation]);
  
    return <Prompt when={when} message={handleViewerNavigation} />
};

export default function Viewer() {
    const { classes: viewsClasses } = viewsStyles();
    const { viewId } = useParams();
    const cachedData = getCachedData();
    const view = cachedData.views[viewId];
    
    if (view) {
        console.log("view:", view);
    }
    const entities = cachedData["entities"];
    const viewer = useRef();
    const { onViewLoad, onNewScene, onBusy, onCameraRotate, onCameraChange, onSimDateChange, onError } = ViewerEvents;
    const [progress, setProgress] = useState(0);
    const [isReady, setReady] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [viewHasChanges, setViewHasChanges] = useState(false);
	const viewerHistory = useSelector((state: any) => state.main.viewerHistory);
    const { newScene, layers, inputs, view:_view } = viewerHistory;
    const [editedData, setEditedData] = useState<any>(null);
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();

    useEffect(() => {
        const update = (data: any)  => {
            const _data = JSON.parse(JSON.stringify({
                newScene: data.newScene,
                layers: data.layers,  
                inputs: data.inputs, 
                view: {
                    description: "",
                    name: "",
                    ...data.view
                }
            }));
            setEditedData(_data);
            dispatch(setData(JSON.parse(JSON.stringify(_data))));
        }
        const onViewLoad = (data: any) => {
            if(isLoaded) return;
            setIsLoaded(true);
            update(data);
        };

        ViewerEvents.on("onViewLoad", onViewLoad);
        ViewerEvents.on("onViewSave", update);

        return () => {
            ViewerEvents.removeEventListener("onViewLoad", onViewLoad);
            ViewerEvents.removeEventListener("onViewSave", update);
        };
    }, [isLoaded]);

    const omittedKeys = [
        'tube_legend_radius', 
        'tube_legend_radius_max', 
        'tube_legend_radius_min', 
        'tube_legend_range_max', 
        'tube_legend_range_min', 
        'tube_legend_range_step',
        'color_legend_range_max',
        'color_legend_range_min',
        'color_legend_attribute_index'
    ]
    const compactObject = (data: any): any => {
        if (!data || typeof data !== 'object') return data;
      
        return Object.keys(data).reduce(function(accumulator: any, key: string) {
          const isObject = data[key] && typeof data[key] === 'object';
          const value = isObject ? compactObject(data[key]) : data[key];
          const isEmptyObject = isObject && (!value || !Object.keys(value).length);
          if (value === undefined || isEmptyObject) {
            return accumulator;
          }
      
          return Object.assign(accumulator, {[key]: value});
        }, {});
    }
    function objectDifference(object:any, base:any) {
        function changes(object:any, base:any) {
            return _.transform(object, function(result:any, value: any, key: string) {
                if (!base || !_.isEqual(value, base[key]) && !omittedKeys.includes(key)) {
                    result[key] = (_.isObject(value) && base && _.isObject(base[key])) ? changes(value, base[key]) : value;
                }
            });
        }
        return compactObject(changes(object, base));
    }

    const removeDeepByKey = (obj: any) => {
        return _.transform(obj, function(result: any, value: any, key: any) {
            if (_.isObject(value)) {
                value = removeDeepByKey(value);
            }
            if (!omittedKeys.includes(key)) {
                _.isArray(obj) ? result.push(value) : result[key] = value;
            }
        });
    };
    
    const  objectsHaveDifference = (object:any, base:any = {}) => {
        return !_.isEqual(removeDeepByKey(object), removeDeepByKey(base))
    }
    useEffect(() => {
        const data = JSON.parse(JSON.stringify({
            newScene, 
            layers, 
            inputs, 
            view:_view
        }));
        const _diff = objectDifference(data, editedData);
        const update = editedData && objectsHaveDifference(editedData, data);
        update !== viewHasChanges && setViewHasChanges(update);
        console.log(update, data, editedData, '...................................difference newScene............................................')
        console.log(_diff, '..................................._diff............................................')
    }, [newScene, layers, inputs, _view, editedData, viewHasChanges]);

    useEffect(() => {
        const handleProgress = (curProgress: number) => {
            setProgress(curProgress);
        };
        ViewerEvents.on("projects_progress", handleProgress);
        return () => {
            ViewerEvents.removeEventListener("projects_progress", handleProgress);
        };
    }, []);

    useEffect(() => {
        if(!view) {
            dispatch(resetActiveProjectData());
            return;
        }

        dispatch(setActiveProjectData({
            activeProject: view.project,
            projectAccessLevel: getAccessLevel(view.project)
        }));
    }, [view])


    useEffect(() => {
        if (cachedData.loaded) {
            if (Boolean(config.api.filters === "true") && view) {
                const pending = cachedData["pending"];
                const projectId = view.project;
                const currentProject = cachedData["entities"][projectId];
                ViewerEvents.currentProject = currentProject;
                let updateList: any = [view.project];
                if (!Object.keys(pending).includes(view["ID"])) {
                    updateList.push(view.ID);
                }

                let entityContainer = [];
                const layers: any = {};
                if (view["entity_container"] !== "") {
                    entityContainer = view["entity_container"].split(" ");
                    entityContainer = Utils.cleanArray(entityContainer);
                    entityContainer.forEach((layerId: string) => {
                        const layer = findElement(view.project, layerId);
                        console.log("layer:", layer);
                        if (layer) {
                            if (layer["view_type"]) {
                                updateList.push(layer["view_type"]);
                                console.log(`Adding ${layer["view_type"]} to list of updates...`);
                            }
                            if (layer["file_type"]) {
                                const entityType = defaultViewProgram[layer["file_type"]];
                                if (entityType) {
                                    updateList.push(entityType);
                                    console.log(`Adding ${entityType} to list of updates...`);
                                }
                            }
                        }
                    });
                }

                updateList = updateList.concat(entityContainer);
                let pendingEntities = updateList.length;
                const entitiesToUpdate:any[] = [];
                updateList.forEach((ID: string) => {
                    ApiUtils.getEntity(ID, view.project, (entity: any) => {
                        let type = "entities";
                        if (entity["view_type"] === "container") {
                            type = "views";
                        }
                        if (entityContainer.includes(entity["ID"])) {
                            layers[entity["ID"]] = entity;
                        }
                        entitiesToUpdate.push({
                            type, 
                            projectId:view.project, 
                            entity
                        });
                        addToCurrentProject(entity);
                        pendingEntities--;
                        if (pendingEntities === 0) {
                            ViewerEvents.layers = layers;
                            console.log("*****updated the view and the objects....");
                            setReady(true);
                        }
                    });
                });
                updateEntitiesToCache(entitiesToUpdate);
            } else {
                ViewerEvents.projectId = view ? view["project"] : "";
                setReady(true);
            }
        }
    }, [view, cachedData]);
    useEffect(() => {
        if (view) {
            ViewerEvents.view = view;
        }
        if (view && viewer.current && ViewerEvents && isReady) {
            ViewerEvents.setViewer(viewer.current);
            ViewerEvents.initialize(view, entities);
        }
        // return () => {
        //     ViewerEvents.emit("destroyView", {});
        // };
    }, [view, viewer, entities, isReady]);

    if (!cachedData.loaded) {
        return <CircularProgressWithLabel className={classes.CircularProgress} value={progress || 0} />;
    }
    if (!view) {
        ViewerEvents.destroy();
        return <Redirect to="/views" />;
    }
    if (!isReady/* || viewer.current*/ || viewer.current === null) {
        return <CircularProgress className={classes.CircularProgress} />;
    }

    const pending = cachedData["pending"];
    const newView: any = Object.values(pending).find((object: any) => object.action === "add" && object.ID === viewId);
    const isNew = newView ? true : false;
    const json = { layers: {} };
    const projectId = view.project;

    const ViewerContextValue = {
        editedData, 
        setEditedData,
        viewHasChanges
    }

    return (
        <ViewerContext.Provider value={ViewerContextValue}>
            <ViewerLeavingGuard
                when={!!viewHasChanges}
                navigate={path => history.push(path)}
                shouldBlockNavigation={location => {
                    return true
                 }}
            />
            <div className={`voxelViewer  ${viewsClasses.root}`} style={{ backgroundColor: "rgb(100, 100, 100)", overflowY: "hidden" }}>
                <Panels />
                {isNew ? (
                    <EmbeddedViewer
                        /* ref is used to point to the instance of the EmbeddedViewer and assigned to variable called this.viewer */
                        ref={viewer}
                        requestAuthToken={requestAuthToken}
                        projectId={projectId}
                        streamingUrl={config.viewer.streamingUrl}
                        restUrl={config.api.baseUrl}
                        restEndpoints={config.api.endpoints}
                        style={{ width: "100%", height: "100%" }}
                        showControlsInView={true}
                        onNewScene={onNewScene}
                        onBusy={onBusy}
                        onViewLoad={onViewLoad}
                        onCrossSectionChange={CrossSectionBounds.onCrossSectionChange}
                        onCameraRotate={onCameraRotate}
                        onCameraChange={onCameraChange}
                        onSimDateChange={onSimDateChange}
                        viewJSON={json}
                        project={entities[projectId]}
                    />
                ) : (
                    <EmbeddedViewer
                        ref={viewer}
                        requestAuthToken={requestAuthToken}
                        projectId={projectId}
                        streamingUrl={config.viewer.streamingUrl}
                        restUrl={config.api.baseUrl}
                        restEndpoints={config.api.endpoints}
                        style={{ width: "100%", height: "100%" }}
                        showControlsInView={true}
                        viewId={viewId}
                        onNewScene={onNewScene}
                        onBusy={onBusy}
                        onViewLoad={onViewLoad}
                        onCrossSectionChange={CrossSectionBounds.onCrossSectionChange}
                        onCameraRotate={onCameraRotate}
                        onCameraChange={onCameraChange}
                        onSimDateChange={onSimDateChange}
                        onError={onError}
                        project={entities[projectId]}
                    />
                )}
            </div>
        </ViewerContext.Provider>
    );
}
